import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, FileText, Settings, LogOut, ChevronRight,
  Building2, Package, Menu, X, User
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/orders/new', icon: FileText, label: 'New Order' },
  { to: '/settings', icon: Settings, label: 'Settings', adminOnly: true },
];

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/orders/new': 'New Order',
  '/settings': 'Settings',
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const roleColors = {
    SUPER_ADMIN: 'bg-yellow-500',
    ADMIN: 'bg-blue-500',
    BROKER: 'bg-green-500',
  };

  const isAdmin = user?.role !== 'BROKER';
  const visibleNavItems = navItems.filter(item => !item.adminOnly || isAdmin);

  // Determine page title for mobile topbar
  const pageTitle = Object.entries(PAGE_TITLES).find(([path]) =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  )?.[1] || 'BillMaster';

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-slate-900 flex flex-col transform transition-transform duration-200
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
            <Building2 size={18} className="text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-sm font-['Manrope']">BillMaster</div>
            <div className="text-slate-400 text-xs">Cloud ERP</div>
          </div>
          <button className="ml-auto text-slate-400 lg:hidden p-1" onClick={() => setMobileOpen(false)}>
            <X size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
              onClick={() => setMobileOpen(false)}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-white/5">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-medium truncate">{user?.name}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`w-1.5 h-1.5 rounded-full ${roleColors[user?.role] || 'bg-gray-400'}`} />
                <span className="text-slate-400 text-xs">{user?.role?.replace('_', ' ')}</span>
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-md text-xs transition-colors"
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 shadow-sm">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-md"
            data-testid="mobile-menu-btn"
          >
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
              <Building2 size={13} className="text-white" />
            </div>
            <span className="font-semibold text-slate-800 text-sm font-['Manrope']">{pageTitle}</span>
          </div>
          <div className="w-8" />
        </div>

        {/* Content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 page-enter pb-20 lg:pb-6">
          {children}
        </main>
      </div>

      {/* Bottom Navigation — mobile only */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200 flex items-stretch shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
        {visibleNavItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors
              ${isActive ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`
            }
            data-testid={`bottom-nav-${label.toLowerCase().replace(' ', '-')}`}
          >
            {({ isActive }) => (
              <>
                <div className={`p-1.5 rounded-lg transition-colors ${isActive ? 'bg-blue-50' : ''}`}>
                  <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                </div>
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
